import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Loader2, ArrowRight, X, Clock, TrendingUp } from "lucide-react";
import { getProducts, resolveImg } from "../lib/api";

const RECENT_KEY = "vr_recent_searches_v1";
const MAX_RECENT = 6;

// Popular searches — each maps to a direct destination (category / subcategory) so
// clicking takes users straight to the relevant collection instead of a search page.
const POPULAR = [
  { label: "Paper Cups", href: "/category/food-service?sub=single-wall-cups" },
  { label: "Nitrile Gloves", href: "/category/hand-protection?sub=black-nitrile-gloves" },
  { label: "Bin Liners", href: "/category/waste-disposal?sub=black-bin-liners" },
  { label: "Napkins", href: "/category/food-service?sub=quilted-lunch-napkins" },
  { label: "Cling Film", href: "/products?search=cling%20film" },
  { label: "Kraft Bags", href: "/products?search=kraft%20bag" },
  { label: "Hand Soap", href: "/category/chemical-hygiene?sub=liquid-hand-care" },
  { label: "Paper Towels", href: "/category/paper-hygiene?sub=slimfold-paper-towels" },
];

const readRecent = () => {
  try { const s = localStorage.getItem(RECENT_KEY); return s ? JSON.parse(s) : []; }
  catch { return []; }
};
const writeRecent = (arr) => {
  try { localStorage.setItem(RECENT_KEY, JSON.stringify(arr.slice(0, MAX_RECENT))); } catch {}
};
const pushRecent = (term) => {
  const t = term.trim();
  if (!t) return readRecent();
  const prev = readRecent().filter((r) => r.toLowerCase() !== t.toLowerCase());
  const next = [t, ...prev].slice(0, MAX_RECENT);
  writeRecent(next);
  return next;
};

const highlight = (text, q) => {
  if (!text || !q) return text || "";
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx < 0) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-vr-hygiene/40 text-vr-ink rounded px-0.5">{text.slice(idx, idx + q.length)}</mark>
      {text.slice(idx + q.length)}
    </>
  );
};

export const LiveSearch = ({ variant = "hero", testIdPrefix = "hero-search", onNavigate }) => {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recent, setRecent] = useState([]);
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const isCompact = variant === "compact";
  const term = q.trim();

  useEffect(() => { setRecent(readRecent()); }, []);

  useEffect(() => {
    if (term.length < 2) { setItems([]); setTotal(0); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const all = await getProducts({ search: term });
        setTotal(all.length);
        setItems(all.slice(0, 8));
      } catch { setItems([]); setTotal(0); }
      finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [term]);

  useEffect(() => {
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const goToSearch = (target) => {
    const t = (target || term).trim();
    if (!t) return;
    setRecent(pushRecent(t));
    navigate(`/products?search=${encodeURIComponent(t)}`);
    setOpen(false);
    setQ("");
    onNavigate?.();
  };

  const submit = (e) => { if (e) e.preventDefault(); goToSearch(); };

  const pickSuggestion = (label) => { setQ(label); goToSearch(label); };

  const pickPopular = (item) => {
    setRecent(pushRecent(item.label));
    navigate(item.href);
    setOpen(false);
    setQ("");
    onNavigate?.();
  };

  const clearInput = () => { setQ(""); setItems([]); setTotal(0); inputRef.current?.focus(); };

  const clearRecent = () => { writeRecent([]); setRecent([]); inputRef.current?.focus(); };

  const showResults = open && term.length >= 2;
  const showSuggestions = open && term.length < 2;

  const chip = "inline-flex items-center gap-1.5 rounded-full border border-vr-border bg-vr-bg2 hover:bg-white hover:border-vr-brand hover:text-vr-brand transition-colors text-vr-ink text-xs font-medium px-3 py-1.5";

  return (
    <div ref={boxRef} data-testid={testIdPrefix} className="relative w-full">
      <form
        onSubmit={submit}
        className={
          isCompact
            ? "flex items-stretch bg-vr-bg2 rounded-full border border-vr-border overflow-hidden focus-within:border-vr-brand focus-within:bg-white transition-all"
            : "flex items-stretch bg-white rounded-full shadow-2xl shadow-vr-navy/30 overflow-hidden ring-1 ring-white/20"
        }
      >
        <div className={isCompact ? "pl-4 pr-2 grid place-items-center text-vr-dim" : "pl-6 pr-3 grid place-items-center text-vr-brand"}>
          <Search size={isCompact ? 18 : 22} />
        </div>
        <input
          ref={inputRef}
          data-testid={`${testIdPrefix}-input`}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder={isCompact ? "Search products or SKU code (e.g. BS8, coffee cup)…" : "Search 1,700+ products by name, description or SKU…"}
          className={
            isCompact
              ? "flex-1 min-w-0 py-2.5 pr-2 bg-transparent text-vr-ink text-sm placeholder:text-vr-dim/70 focus:outline-none"
              : "flex-1 min-w-0 py-4 pr-2 bg-transparent text-vr-ink text-base placeholder:text-vr-dim/70 focus:outline-none"
          }
          aria-label="Search products"
        />
        {q && (
          <button type="button" onClick={clearInput} data-testid={`${testIdPrefix}-clear`} aria-label="Clear search" className="px-2 text-vr-dim hover:text-vr-ink transition-colors">
            <X size={isCompact ? 16 : 18} />
          </button>
        )}
        <button
          type="submit"
          data-testid={`${testIdPrefix}-submit`}
          className={
            isCompact
              ? "px-4 my-1 mr-1 rounded-full bg-vr-brand text-white font-semibold text-xs hover:bg-vr-branddark transition-colors flex items-center gap-1"
              : "px-5 md:px-7 my-1.5 mr-1.5 rounded-full bg-vr-brand text-white font-semibold text-sm hover:bg-vr-branddark transition-colors flex items-center gap-1.5"
          }
        >
          Search{!isCompact && <ArrowRight size={16} />}
        </button>
      </form>

      <AnimatePresence>
        {showResults && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18 }}
            data-testid={`${testIdPrefix}-results`}
            className="absolute left-0 right-0 mt-2 rounded-2xl bg-white border border-vr-border shadow-2xl overflow-hidden z-40"
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-vr-border bg-vr-bg2">
              <div className="text-xs uppercase tracking-[0.15em] font-semibold text-vr-dim">
                {loading ? "Searching…" : total > 0 ? `${total} match${total === 1 ? "" : "es"}` : "No matches"}
              </div>
              {loading && <Loader2 size={14} className="animate-spin text-vr-brand" />}
            </div>

            <div data-testid={`${testIdPrefix}-list`} className="max-h-[420px] overflow-y-auto divide-y divide-vr-border">
              {!loading && items.length === 0 && (
                <div className="px-5 py-8 text-center text-sm text-vr-dim">
                  Nothing matched <span className="font-semibold text-vr-ink">“{term}”</span>. Try a broader term or the SKU code.
                </div>
              )}
              {items.map((p) => (
                <Link
                  key={p.sku}
                  to={`/product/${p.slug || p.sku}`}
                  onClick={() => { setRecent(pushRecent(term)); setOpen(false); setQ(""); onNavigate?.(); }}
                  data-testid={`${testIdPrefix}-result-${p.sku}`}
                  className="flex items-start gap-4 px-5 py-3.5 hover:bg-vr-bg2 transition-colors"
                >
                  <div className="w-14 h-14 rounded-lg bg-[#e6f4fc] flex items-center justify-center overflow-hidden shrink-0">
                    {p.images?.[0] && <img src={resolveImg(p.images[0])} alt="" className="max-w-full max-h-full object-contain" loading="lazy" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] font-mono text-vr-brand">{highlight(p.sku, term)}</div>
                    <div className="text-sm text-vr-ink font-semibold line-clamp-1">{highlight(p.name, term)}</div>
                    {p.description && (
                      <div className="text-xs text-vr-dim line-clamp-2 mt-0.5">{highlight(p.description, term)}</div>
                    )}
                  </div>
                </Link>
              ))}
            </div>

            {total > items.length && (
              <button
                type="button"
                onClick={submit}
                data-testid={`${testIdPrefix}-view-all`}
                className="w-full flex items-center justify-center gap-2 px-5 py-3.5 bg-vr-navy text-white font-semibold text-sm hover:bg-vr-brand transition-colors"
              >
                View all {total} matches <ArrowRight size={15} />
              </button>
            )}
          </motion.div>
        )}

        {showSuggestions && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18 }}
            data-testid={`${testIdPrefix}-suggestions`}
            className="absolute left-0 right-0 mt-2 rounded-2xl bg-white border border-vr-border shadow-2xl overflow-hidden z-40"
          >
            {recent.length > 0 && (
              <div className="px-5 py-4 border-b border-vr-border" data-testid={`${testIdPrefix}-recent`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] font-semibold text-vr-dim">
                    <Clock size={13} /> Recent searches
                  </div>
                  <button
                    type="button"
                    onClick={clearRecent}
                    data-testid={`${testIdPrefix}-recent-clear`}
                    className="text-[11px] text-vr-dim hover:text-red-500 transition-colors"
                  >
                    Clear
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {recent.map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => pickSuggestion(r)}
                      data-testid={`${testIdPrefix}-recent-${r.replace(/\s+/g, "-").toLowerCase()}`}
                      className={chip}
                    >
                      <Clock size={11} className="text-vr-dim" /> {r}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="px-5 py-4" data-testid={`${testIdPrefix}-popular`}>
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] font-semibold text-vr-dim mb-3">
                <TrendingUp size={13} /> Popular searches
              </div>
              <div className="flex flex-wrap gap-2">
                {POPULAR.map((p) => (
                  <button
                    key={p.label}
                    type="button"
                    onClick={() => pickPopular(p)}
                    data-testid={`${testIdPrefix}-popular-${p.label.replace(/\s+/g, "-").toLowerCase()}`}
                    className={chip}
                  >
                    <TrendingUp size={11} className="text-vr-brand" /> {p.label}
                  </button>
                ))}
              </div>
              <p className="mt-3 text-[11px] text-vr-dim">Start typing to search across product names, descriptions and SKU codes.</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
